// src/components/network/layouts/NetworkRightSidebar.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  getDocs, doc, getDoc
} from 'firebase/firestore';
import MessagePreview from '../messaging/MessagePreview';

const NetworkRightSidebar = () => {
  const { currentUser } = useAuth();
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  
  // Fetch recent conversations
  useEffect(() => {
    const fetchRecentConversations = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Query recent conversations
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', currentUser.uid),
          orderBy('lastMessageAt', 'desc'),
          limit(5)
        );
        
        const conversationsSnapshot = await getDocs(conversationsQuery);
        
        // Process each conversation
        const conversationsData = [];
        let unreadCount = 0;
        
        for (const conversationDoc of conversationsSnapshot.docs) {
          const conversation = conversationDoc.data();
          
          // Find the other participant
          const otherParticipantId = conversation.participants.find(id => id !== currentUser.uid);
          
          // Get participant info (could be user or business)
          let participantInfo = { id: otherParticipantId, name: 'Unknown' };
          
          try {
            // First try to get business info
            const businessRef = doc(db, 'businesses', otherParticipantId);
            const businessSnap = await getDoc(businessRef);
            
            if (businessSnap.exists()) {
              const businessData = businessSnap.data();
              participantInfo = {
                id: otherParticipantId,
                name: businessData.name,
                type: 'business',
                logoUrl: businessData.businessLogoUrl || null,
                industry: businessData.industry || null
              };
            } else {
              // Try to get user info
              const userRef = doc(db, 'users', otherParticipantId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                participantInfo = {
                  id: otherParticipantId,
                  name: userData.displayName || userData.email,
                  type: 'user',
                  email: userData.email,
                  photoURL: userData.photoURL || null
                };
              }
            }
          } catch (err) {
            console.error('Error fetching participant info:', err);
          }
          
          const userUnreadCount = conversation.unreadCount?.[currentUser.uid] || 0;
          unreadCount += userUnreadCount;
          
          conversationsData.push({
            id: conversationDoc.id,
            ...conversation,
            lastMessageAt: conversation.lastMessageAt?.toDate() || new Date(),
            participant: participantInfo,
            unreadCount: userUnreadCount
          });
        }
        
        setRecentConversations(conversationsData);
        setTotalUnread(unreadCount);
      } catch (err) {
        console.error('Error fetching conversations:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentConversations();
    
    // Set up an interval to refresh conversations
    const intervalId = setInterval(fetchRecentConversations, 60000); // Every minute
    
    return () => clearInterval(intervalId);
  }, [currentUser]);
  
  if (!currentUser) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
        <MessageSquare size={24} className="mx-auto text-gray-300 mb-2" />
        <h3 className="text-sm font-medium text-gray-900 mb-1">Network Messages</h3>
        <p className="text-xs text-gray-500 mb-3">
          Sign in to connect with businesses
        </p>
        <Link
          to="/login"
          className="text-[#190192] text-sm hover:underline"
        >
          Sign In
        </Link>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center">
          <MessageSquare size={18} className="text-[#190192] mr-2" />
          <h3 className="font-medium text-gray-900">Messages</h3>
          {totalUnread > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-[#190192] text-white rounded-full">
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 text-gray-500 hover:text-gray-700 lg:block hidden"
        >
          {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
      </div>
      
      {/* Content */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex p-2">
                  <div className="w-8 h-8 bg-gray-200 rounded-full mr-2"></div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentConversations.length === 0 ? (
            <div className="p-4 text-center flex-1 flex flex-col justify-center">
              <p className="text-sm text-gray-500 mb-1">No messages yet</p>
              <p className="text-xs text-gray-400 mb-3">
                Start connecting with businesses
              </p>
              <Link
                to="/network/messages/new"
                className="text-[#190192] text-sm hover:underline"
              >
                Start a conversation
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentConversations.map(conversation => (
                <Link key={conversation.id} to={`/network/messages/${conversation.id}`}>
                  <MessagePreview conversation={conversation} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NetworkRightSidebar;